import {Injectable} from "@angular/core";
import {LoggerService} from "../common/logger.service";
import {UsanaObject} from "../usana-object";
import {catchError, map, mergeMap} from "rxjs/operators";
import {forkJoin, from, Observable, throwError} from "rxjs";
import {GqlCustomerService} from "../../checkout/graphql/customer/gql-customer.service";
import {Constants} from "../constants";
import {SessionService} from "../common/session.service";
import {LocaleUtil} from "../util/locale-util";
import {WrappedBehaviorSubject} from "../cache/wrapped-behavior-subject";
import {ILocaleProperties, ILocaleView, LocaleType} from "./locale-models";
import {GqlLocaleService} from "./graphql/gql-locale.service";
import {TranslateService} from "../translation/translate.service";
import {LocaleSwitcherComponent} from "./locale-switcher.component";
import {EnrollCustomerCacheService} from "../enroll/associate/enroll-customer-cache.service";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";

@Injectable({
    providedIn: 'root'
})
export class LocaleSwitcherService extends UsanaObject {

    readonly localePropertiesSubject: WrappedBehaviorSubject<ILocaleProperties[]> = new WrappedBehaviorSubject();
    bsModalRef: BsModalRef;

    constructor(
        loggerService: LoggerService,
        private sessionService: SessionService,
        private gqlCustomerService: GqlCustomerService,
        private gqlLocaleService: GqlLocaleService,
        private translateService: TranslateService,
        private enrollCustomerCacheService: EnrollCustomerCacheService,
        private modalService: BsModalService
    ) {
        super(loggerService);
    }

    getLocalesForSwitcher(localeType: LocaleType): Observable<any> {
        let localeServiceMethod: () => Observable<ILocaleView[]>;
        switch (localeType) {
            case LocaleType.ENROLL_PC:
                localeServiceMethod = this.gqlLocaleService.getEnrollPcLocales.bind(this.gqlLocaleService);
                break;
            case LocaleType.ENROLL_AFFILIATE:
                localeServiceMethod = this.gqlLocaleService.getEnrollAffiliateLocales.bind(this.gqlLocaleService);
                break;
            default:
                localeServiceMethod = this.gqlLocaleService.getRetailLocales.bind(this.gqlLocaleService);
        }

        return localeServiceMethod().pipe(
            mergeMap((localeViewList) => {
                const localeObservables = localeViewList.map((localeView) =>
                    this.translateLocale(localeView)
                );

                return forkJoin(localeObservables).pipe(
                    map((translatedLocaleViews) => this.sortLocales(translatedLocaleViews))
                );
            }),
            catchError((error) => throwError(error))
        );
    }

    // translate the locale fields
    private translateLocale(localeView: any): Observable<any> {
        const continentDisplay$ = from(this.translateService.translate(localeView.continentTag));
        const countryDisplay$ = from(this.translateService.translate(localeView.countryDisplayTag));
        const languageDisplay$ = from(this.translateService.translate(localeView.languageDisplayTag));

        return forkJoin([continentDisplay$, countryDisplay$, languageDisplay$]).pipe(
            map(([translatedContinent, translatedCountryDisplay, translatedLanguageDisplay]) => {
                localeView.continent = translatedContinent;
                localeView.countryDisplay = translatedCountryDisplay;
                localeView.languageDisplay = translatedLanguageDisplay;
                return localeView;
            })
        );
    }

    // sort the translated locales
    private sortLocales(localeViews: any[]): any[] {
        return localeViews.sort((a, b) => {
            const continentComparison = a.continent.localeCompare(b.continent);
            if (continentComparison !== 0) {
                return continentComparison;
            }

            const countryComparison = a.countryDisplay.localeCompare(b.countryDisplay);
            if (countryComparison !== 0) {
                return countryComparison;
            }

            return a.languageDisplay.localeCompare(b.languageDisplay);
        });
    }

    changeLocale(locale: string) {
        //clear all possible checkout data for cached order and payment info
        sessionStorage.removeItem(Constants.CART_ORDER_ID_SESSION_STORAGE);
        sessionStorage.removeItem(Constants.ONE_TIME_USE_PAYMENT_SESSIONSTORAGE);
        sessionStorage.removeItem(Constants.CC_CVV_SUCCESS);

        this.gqlCustomerService.removeRetailCookies().subscribe(() => {
            //sets the cookie to the new locale before location refresh so selected locale is present in url when refresh finishes
            this.sessionService.updateLocale(LocaleUtil.getUsanaLocale(locale));
            window.location.reload();
        });
    }

    openLocaleSwitcher() {
        this.bsModalRef = this.modalService.show(LocaleSwitcherComponent, Object.assign({}, {class: 'modal-md'}));
        const updatedLocaleData = this.bsModalRef.content.updatedLocaleData;
        // page is refreshing, no need to unsubscribe
        updatedLocaleData.asObservable().subscribe((locale: ILocaleProperties) => {
            //Changing locales for retail, reset the signup locale, so it will go back to this default
            this.enrollCustomerCacheService.clearSignupLocales();
            this.changeLocale(locale?.usanaLocale);
        });
    }
}

